import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Spinner, Table, TableHeader, TableColumn, TableBody, TableRow, TableCell,
  User, Chip, Select, SelectItem, Button, Input, Tooltip
} from "@heroui/react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  useDisclosure,
} from "@heroui/react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "@heroui/modal";

import { getCallPlanningData, getAllVolunteerAvailability, postCallPlanningData } from "../../api/api";
import { Key } from "@react-types/shared";
import toast from 'react-hot-toast';
import { MdFilterAlt } from "react-icons/md";
import { IoMdRefresh } from "react-icons/io";
import { MdEditCalendar } from "react-icons/md";
import { FaSave } from "react-icons/fa";
import { MdAssignmentAdd } from "react-icons/md";
import { FaUser } from "react-icons/fa";
import {DatePicker} from "@heroui/react";
import { CalendarDate, parseDate } from "@internationalized/date";
import {I18nProvider} from "@react-aria/i18n";
import { useLocation } from "react-router-dom";

const statusColorMap: Record<string, "default" | "success" | "danger" | "warning"> = {
  Planned: "default",
  Held: "success",
  "Not Held": "warning",
  "Held - Staff Review": "danger",
  "Not Held - Left Message": "warning",
};

const mapAssigneeLabelToKey = (
  label: string,
  volunteers: { key: string; label: string }[]
) => volunteers.find((v) => v.label === label)?.key || label;

const mapAssigneeKeyToLabel = (
  key: string,
  volunteers: { key: string; label: string }[]
) => volunteers.find((v) => v.key === key)?.label || key;

const status = [
  { key: "Planned", label: "Planned" },
  { key: "Held", label: "Held" },
  { key: "Held - Staff Review", label: "Held - Staff Review" },
  { key: "Not Held", label: "Not Held" },
  { key: "Not Held - Left Message", label: "Not Held - Left Message" },
];

const StaffHome: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState(false);

  const location = useLocation();
  const isStaffPage = location.pathname.includes("/staff/staffcalls");

  // Get the current date and calculate the start and end of the week for Date Picker
  const today = new Date();
  const thisMonday = new Date(today);
  thisMonday.setDate(today.getDate() - ((today.getDay() + 6) % 7)); // get Monday
  const thisSunday = new Date(thisMonday);
  thisSunday.setDate(thisMonday.getDate() + 6); // get Sunday
  const [dateRange, setDateRange] = useState<{ start: CalendarDate; end: CalendarDate }>({
    start: parseDate(thisMonday.toISOString().split("T")[0]),
    end: parseDate(thisSunday.toISOString().split("T")[0]),
  });

  const {
    isOpen: isModalOpen,
    // onOpen: openModal,
    onOpenChange: onModalChange
  } = useDisclosure();

  const {
    isOpen: isDrawerOpen,
    // onOpen: openDrawer,
    onOpenChange: onDrawerChange
  } = useDisclosure();

  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const {
    isOpen: isDetailModalOpen,
    onOpen: openDetailModal,
    onOpenChange: onDetailModalChange
  } = useDisclosure();


  const [editableRows, setEditableRows] = useState<any[]>([]);
  const [availableVolunteers, setAvailableVolunteers] = useState<any[]>([]);
  const [showStaffCalls, ] = useState<boolean>(isStaffPage);

  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set(["Planned", "Held", "Not Held", "Not Held - Left Message", "Held - Staff Review"]));
  const didRunRef = useRef(false);

  const [modalDate, setModalDate] = useState<CalendarDate | null>(null);
  const [modalSubject, setModalSubject] = useState("");
  const [modalSubjectRearranged, setModalSubjectRearranged] = useState("");
  const [modalStatus, setModalStatus] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (didRunRef.current) return;
    didRunRef.current = true;
    fetchDataWithRange(dateRange.start, dateRange.end);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const rowsToSave = editableRows;
  
    try {
      await postCallPlanningData(rowsToSave);
      await fetchDataWithRange(dateRange.start, dateRange.end);
      toast.success("Call plan saved successfully!");
    } catch (error) {
      toast.error("Failed to save call plan.");
    } finally {
      setSaving(false);
    }
  };

  const autoAssignHandler = () => {
    const rows = editableRows;
    const volunteers = availableVolunteers;
  
    // Build maxCalls and usage maps
    const assigneeLimits: Record<string, number | null> = {};
    const assigneeUsage: Record<string, number> = {};
  
    volunteers.forEach((vol) => {
      const maxCalls = vol.maxCalls ? parseInt(vol.maxCalls, 10) : 10;
      assigneeLimits[vol.key] = isNaN(maxCalls) ? null : maxCalls;
      assigneeUsage[vol.key] = 0;
    });
  
    const shuffledRows = [...rows].sort(() => Math.random() - 0.5);
    const updatedRows = shuffledRows.map((row) => {
      if (
        row.staff_only === "1" || row.staff_only === 1 ||
        row.eventstatus?.toLowerCase() !== "planned"
      ) {
        return row;
      }
  
      const previousAssigneeRaw = row.previous_assignee || "";
      const previousKey = mapAssigneeLabelToKey(previousAssigneeRaw, volunteers);
  
      // Filter out the previous assignee and anyone at or above their max limit
      const eligible = volunteers
        .filter(({ key }) => key !== previousKey)
        .filter(({ key }) => {
          const limit = assigneeLimits[key];
          return limit === null || assigneeUsage[key] < limit;
        });
  
      if (eligible.length === 0) {
        // All limits reached — assign fallback
        return {
          ...row,
          assigned_user_id: "19x218",
          assigned_user_name: "Unassigned",
        };
      }
  
      // Sort eligible by current usage (lowest first)
      eligible.sort((a, b) => assigneeUsage[a.key] - assigneeUsage[b.key]);
  
      const chosen = eligible[0];
      assigneeUsage[chosen.key]++;
  
      return {
        ...row,
        assigned_user_id: chosen.key,
        assigned_user_name: chosen.label,
      };
    });
  
    setEditableRows(
      rows.map((original) => updatedRows.find((row) => row.id === original.id) || original)
    );
  };
  
  
  const handleAssigneeChange = (id: string, newKey: string) => {
    const newLabel = mapAssigneeKeyToLabel(newKey, availableVolunteers);
    setEditableRows((prev) =>
      prev.map((row) =>
        row.id === id ? { ...row, assigned_user_id: newKey, assigned_user_name: newLabel } : row
      )
    );    
  };

  const handleDateChange = (id: string, newDate: string) => {
    setEditableRows((prev) =>
      prev.map((row) =>
        row.id === id ? { ...row, due_date: newDate, date_start: newDate,}: row
      )
    );
  };
  
  const handleStartDateChange = (newStart: CalendarDate | null) => {
    if (newStart) {
      const newEnd = newStart.add({ days: 6 });
      setDateRange({ start: newStart, end: newEnd });
      fetchDataWithRange(newStart, newEnd);
    }
  };

  const fetchDataWithRange = async (start: CalendarDate, end: CalendarDate) => {
    setLoading(true);
    const startStr = start.toString();
    const endStr = end.toString();
  
    try {
      const [callDataResult, volunteerDataResult] = await Promise.allSettled([
        getCallPlanningData({ startdate: startStr, enddate: endStr }),
        getAllVolunteerAvailability({ startdate: startStr, enddate: endStr }),
      ]);
  
      let anyFailure = false;
  
      if (callDataResult.status === "fulfilled") {
        setEditableRows(callDataResult.value.assigned_calls || []);
      } else {
        anyFailure = true;
      }
  
      if (volunteerDataResult.status === "fulfilled") {
        setAvailableVolunteers(volunteerDataResult.value || []);
      } else {
        anyFailure = true;
      }
  
      if (anyFailure) {
        toast.custom((t) => (
          <div className="bg-white border border-red-200 shadow-lg mt-[40vh] p-10 rounded-md text-center max-w-sm mx-auto">
            <p className="text-red-600 font-semibold mb-2">Some data failed to load.</p>
            <Button
              color="danger"
              size="sm"
              className="mx-auto"
              onClick={() => {
                toast.dismiss(t.id);
                fetchDataWithRange(start, end);
              }}
            >
              Retry
            </Button>
          </div>
        ), {
          position: "top-center",
          duration: Infinity,
        });
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      toast.error("An unexpected error occurred while fetching data.");
    }
  };

  const currentRows = editableRows;

  const filteredRows = currentRows.filter((row) => {
    const isStaffOnly = row.staff_only === "1" || row.staff_only === 1;
  
    // If showStaffCalls is true, only show staff calls
    // If false, exclude staff calls
    if (showStaffCalls !== isStaffOnly) return false;
  
    const matchesSearch =
      row.neighbour?.toLowerCase().includes(searchValue.toLowerCase()) ||
      row.assigned_user_name?.toLowerCase().includes(searchValue.toLowerCase());
  
    const matchesStatus = statusFilter.has(row.eventstatus);
  
    return matchesSearch && matchesStatus;
  });

  const assigneeCounts = currentRows.reduce((acc: Record<string, number>, row) => {
    const assignee = row.assigned_user_name || "Unassigned";
    acc[assignee] = (acc[assignee] || 0) + 1;
    return acc;
  }, {});

  const assigneeLimits: Record<string, string | number> = {};

  const volunteers = availableVolunteers;
  volunteers.forEach((vol) => {
    const label = vol.label || "Unassigned";
    const maxCalls = vol.maxCalls ? parseInt(vol.maxCalls, 5) : 5;
    assigneeLimits[label] = isNaN(maxCalls) ? "" : maxCalls;
  });
  
  // Include fallback Unassigned entry if needed
  assigneeLimits["Unassigned"] = assigneeLimits["Unassigned"] || "";

  const renderCell = (user: any, columnKey: Key) => {
    const cellValue = user[columnKey];
    switch (columnKey) {
      case "name":
        return (
          <Link to={`/staff/record/${user.id}`}>
            <User
              name={user.neighbour}
              description={user.subject}
              className="hover:underline text-primary"
            />
          </Link>
        );

      case "due_date":
        return (
          <div className="h-[48px] flex items-center">
            <Input
              type="date"
              size="sm"
              radius="sm"
              value={user.due_date || ""}
              onChange={(e) => handleDateChange(String(user.id), e.target.value)}
              className="w-[140px]"
              isReadOnly
            />
          </div>
        );

        case "assignee": {
          if (user.staff_only === "1" || user.staff_only === 1) {
            return (
              <div className="h-[48px] flex items-center font-semibold text-red-600">
                STAFF CALL
              </div>
            );
          }
        
          const volunteers = availableVolunteers;
          const assignedLabel = user.assigned_user_name;
          const assignedKey = mapAssigneeLabelToKey(assignedLabel, volunteers);
          const isKnownAssignee = volunteers.some((v) => v.key === assignedKey);
          const previousAssignee = user.previous_assignee;
          const isSameAsPrevious = previousAssignee && assignedLabel === previousAssignee;
        
          const dropdownOptions = isKnownAssignee
            ? volunteers
            : [
                ...volunteers,
                {
                  key: assignedKey,
                  label:
                    assignedLabel === "API User"
                      ? "Unassigned"
                      : `${assignedLabel} (Unavailable)` || "Unassigned",
                },
              ];
        
          return (
            <div className="flex flex-col gap-1">
              <Select
                color={
                    !isKnownAssignee
                    ? "danger"
                    : isSameAsPrevious
                    ? "warning"
                    : "default"
                }
                size="sm"
                aria-label={`Assignee for ${user.neighbour || "unknown"}`}
                selectedKeys={new Set([assignedKey])}
                onSelectionChange={(keys) =>
                  handleAssigneeChange(String(user.id), Array.from(keys)[0] as string)
                }
              >
                {dropdownOptions.map((vol) => (
                  <SelectItem key={vol.key}>{vol.label}</SelectItem>
                ))}
              </Select>
            </div>
          );
        }
        
      case "status":
        return (
          <Chip
            className="capitalize"
            color={statusColorMap[user.eventstatus] || "default"}
            size="md"
            variant="flat"
            radius="md"
          >
            {user.eventstatus}
          </Chip>
        );

        case "action":
          return (
            <Tooltip content="Rearrange Call" placement="top">
              <Button
                size="sm"
                variant="light"
                color="primary"
                className="focus:outline-none hover:border-white"
                isIconOnly
                onClick={() => {
                  setSelectedRow(user);
                  setModalSubject(user.subject || "");
                  setModalSubjectRearranged(user.subject || "");
                  setModalDate(user.due_date ? parseDate(user.due_date) : null);
                  setModalStatus(user.eventstatus ? new Set([user.eventstatus]) : new Set());
                  openDetailModal();
                }}
              >
                <MdEditCalendar size={22}/>
              </Button>
            </Tooltip>
          );

      default:
        return cellValue;
    }
  };

  return (
    <div className="bg-white flex flex-col">

      {/* Sidebar + Content Container */}
        {(loading || saving) && (
          <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-80 z-[9998]">
              <Spinner size="lg" color="primary" />
          </div>
          )}

        {/* Main Content */}
        <main className="overflow-y-auto px-2 py-4">
            {/* Week Selector */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-2 gap-4 w-full mx-auto">
              <div className="">
                <I18nProvider locale="en-GB">
                  <div className="flex gap-2 items-end">
                    {/* Start Date Picker (user selectable) */}
                    <DatePicker
                      className="max-w-xs"
                      firstDayOfWeek="mon"
                      label="Date:"
                      labelPlacement="outside-left"
                      value={dateRange.start}
                      onChange={handleStartDateChange}
                    />

                    {/* End Date Picker (auto-set and disabled) */}
                    <DatePicker
                      className="max-w-xs"
                      label="to"
                      labelPlacement="outside-left"
                      value={dateRange.end}
                      isReadOnly
                    />

                  </div>
                </I18nProvider>

                </div>
              <div className="flex gap-2">
                <Input
                    isClearable
                    size="sm"
                    radius="sm"
                    placeholder="Search..."
                    value={searchValue}
                    onValueChange={setSearchValue}
                    className="w-full"
                />
                <Tooltip content="Assignee Details" placement="top">
                <Button
                  className="focus:outline-none hover:border-white"
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="default"
                  onClick={() => onModalChange()}
                >
                  <FaUser size={22}/>
                </Button>
                </Tooltip>
                <Tooltip content="Filters" placement="top">
                <Button
                  className="focus:outline-none hover:border-white"
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="default"
                  onClick={() => onDrawerChange()}
                >
                  <MdFilterAlt size={25}/>
                </Button>
                </Tooltip>
                <Tooltip content="Refresh Data" placement="top">
                <Button
                  className="focus:outline-none hover:border-white"
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="default"
                  onClick={() => fetchDataWithRange(dateRange.start, dateRange.end)}
                >
                  <IoMdRefresh size={25}/>
                </Button>
                </Tooltip>
                <Tooltip content="Save" placement="top">
                <Button
                  className="focus:outline-none hover:border-white"
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="default"
                  onClick={handleSave}
                >
                  <FaSave size={25}/>
                </Button>
                </Tooltip>
                <Tooltip content="Auto Assignment" placement="top">
                <Button
                  className="focus:outline-none hover:border-white"
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="default"
                  onClick={autoAssignHandler}
                >
                  <MdAssignmentAdd size={25}/>
                </Button>
                </Tooltip>
            </div>
          </div>
            {/* Table */}
            <div className="w-full mx-auto">
              <Table
                isHeaderSticky
                aria-label="Call Planner Table"
                classNames={{
                  wrapper: "border-none shadow-none",
                  base: "h-full",
                  table: "overflow-auto",
                }}
              >
                <TableHeader columns={[
                  { name: "NEIGHBOUR NAME", uid: "name" },
                  { name: "Date", uid: "due_date" },
                  { name: "ASSIGNEE", uid: "assignee" },
                  { name: "STATUS", uid: "status" },
                  { name: "ACTIONS", uid: "action" },
                ]}>
                  {(column) => (
                    <TableColumn
                      key={column.uid}
                      align={column.uid === "actions" ? "center" : "start"}
                      className={column.uid === "assignee" ? "min-w-[200px] w-[250px]" : ""}
                    >
                      {column.name}
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody items={filteredRows}>
                  {(item) => (
                    <TableRow key={item.id} className="border-b border-gray-200 border-b-[1px]">
                      {(columnKey) => (
                        <TableCell>{renderCell(item, columnKey)}</TableCell>
                      )}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </main>
          <Drawer isOpen={isDrawerOpen} onOpenChange={onDrawerChange}>
            <DrawerContent>
              {(onClose) => (
                <>
                  <DrawerHeader className="flex flex-col gap-1">Filters</DrawerHeader>
                    <DrawerBody>
                      {/* Status Filter */}
                      <div>
                        <label className="block text-sm font-medium mb-1">Status</label>
                        <Select
                          aria-label="Filter by status"
                          selectionMode="multiple"
                          selectedKeys={statusFilter}
                          onSelectionChange={(keys) =>
                            setStatusFilter(
                              new Set((Array.from(keys) as string[]).length === 0 ? ["all"] : Array.from(keys).map(String))
                            )
                          }
                          size="sm"
                          renderValue={() => "Select Status"}
                          className="w-full"
                        >
                          <SelectItem key="Planned">Planned</SelectItem>
                          <SelectItem key="Held">Held</SelectItem>
                          <SelectItem key="Not Held">Not Held</SelectItem>
                          <SelectItem key="Not Held - Left Message">Not Held - Left Message</SelectItem>
                          <SelectItem key="Not Held - Staff Review">Not Held - Staff Review</SelectItem>
                        </Select>
                      </div>

                      {/* Staff Filter */}
                      {/* <div>
                        <Checkbox
                          size="sm"
                          isSelected={showStaffCalls}
                          onValueChange={setShowStaffCalls}
                        >
                          Staff Calls Only
                        </Checkbox>
                      </div> */}
                    </DrawerBody>
                  <DrawerFooter>
                    <Button color="danger" variant="light" onPress={onClose}>
                      Close
                    </Button>
                  </DrawerFooter>
                </>
              )}
            </DrawerContent>
          </Drawer>

          <Modal isOpen={isModalOpen} onOpenChange={onModalChange}>
            <ModalContent>
              {(onClose) => (
                <>
                  <ModalHeader className="flex flex-col gap-1">Assignee Totals</ModalHeader>
                  <ModalBody>
                    {/* Assignee Totals */}
                    <div className="max-w-screen-lg w-full mx-auto">
                      <Table aria-label="Assignee totals table">
                      <TableHeader>
                        <TableColumn>Assignee</TableColumn>
                        <TableColumn>Assigned Calls</TableColumn>
                        <TableColumn>Max Calls</TableColumn>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(assigneeCounts).map(([name, count]) => (
                          <TableRow key={name}>
                            <TableCell>{name}</TableCell>
                            <TableCell>{count}</TableCell>
                            <TableCell>{assigneeLimits[name] !== undefined ? assigneeLimits[name] : ""}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      </Table>
                    </div>
                  </ModalBody>
                  <ModalFooter>
                    <Button color="danger" variant="light" onPress={onClose}>
                      Close
                    </Button>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Modal>
          <Modal isOpen={isDetailModalOpen} onOpenChange={onDetailModalChange}>
          <ModalContent>
            {(onClose) => (
              <>
                <ModalHeader>Call Rearrange</ModalHeader>
                <ModalBody>
                  {selectedRow ? (
                    <div className="space-y-2 text-sm">
                      <p> Current Call:</p>
                      <Input
                        label="Neighbour"
                        value={selectedRow.neighbour}
                        className="max-w-xs"
                        isReadOnly
                      />
                      <Input
                        label="Subject"
                        value={modalSubject}
                        onValueChange={setModalSubject}
                        className="max-w-xs"
                      />
                      <Select
                        color="default"
                        size="md"
                        variant="bordered"
                        label="Status"
                        selectedKeys={modalStatus}
                        onSelectionChange={(keys) => setModalStatus(keys as Set<string>)}
                        className="max-w-xs"
                      >
                        {status.map((status) => (
                          <SelectItem key={status.key}>{status.label}</SelectItem>
                        ))}
                      </Select>
                      <p className="pt-4">Rearranged Call:</p>
                      <I18nProvider locale="en-GB">
                        <DatePicker
                          label="Rearranged Date"
                          value={modalDate}
                          onChange={setModalDate}
                          className="max-w-xs"
                        />
                      </I18nProvider>
                      <Input
                        label="Subject"
                        value={modalSubjectRearranged}
                        onValueChange={setModalSubjectRearranged}
                        className="max-w-xs"
                      />
                    </div>
                  ) : (
                    <p>No call selected.</p>
                  )}
                </ModalBody>
                <ModalFooter>
                  <Button
                    color="primary"
                    variant="solid"
                    onPress={async () => {
                      if (!selectedRow) return;

                      const updatedCall = {
                        ...selectedRow,
                        subject: modalSubject,
                        eventstatus: Array.from(modalStatus)[0] || null,
                        new_date: modalDate?.toString() || null,
                        rearranged_subject: modalSubjectRearranged,
                        rearrangement: "True",
                      };

                      try {
                        await postCallPlanningData([updatedCall], "true");
                        toast.success("Call updated successfully");
                        onClose(); // Close the modal
                        fetchDataWithRange(dateRange.start, dateRange.end); // Refresh table
                      } catch (error) {
                        toast.error("Failed to update call");
                      }
                    }}
                  >
                    Update
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>
        

        </div>
  );
};

export default StaffHome;
